// src/pages/SuperAdminUpgrade.jsx
import React, { useState } from 'react';
import { FiShield, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { upgradeSuperAdmin } from '../utils/upgradeSuperAdmin';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const SuperAdminUpgrade = () => {
  const [upgradeStatus, setUpgradeStatus] = useState('idle');
  const [error, setError] = useState(null);
  const { currentUser, loadUserProfile, userProfile } = useAuth();
  const navigate = useNavigate();

  const handleUpgrade = async () => {
    if (!currentUser) {
      toast.error('Please log in first');
      return;
    }

    try {
      setUpgradeStatus('running');
      setError(null);
      
      console.log('🔧 Upgrading to super admin...');
      await upgradeSuperAdmin(currentUser.uid);
      
      // Reload user profile to get the new super admin role
      await loadUserProfile(currentUser.uid);
      
      setUpgradeStatus('success');
      toast.success('Successfully upgraded to Super Admin!');
      
      // Redirect to admin panel after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
      
    } catch (err) {
      console.error('Super admin upgrade failed:', err);
      setError(err.message);
      setUpgradeStatus('error');
      toast.error('Upgrade failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <FiShield className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Super Admin Upgrade
            </h1>
            <p className="text-gray-600">
              Upgrade your account to Super Admin for multi-tenant management
            </p>
          </div>

          {currentUser && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-blue-900 mb-2">Current User:</h3>
              <p className="text-blue-700 mb-1">Email: {currentUser.email}</p>
              <p className="text-blue-700">Current Role: {userProfile?.role || 'Loading...'}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-lg">
              <h3 className="font-medium text-purple-900 mb-2">Super Admin Privileges:</h3>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Switch between multiple clinic tenants</li>
                <li>• Manage all tenants and their settings</li>
                <li>• Access cross-tenant reporting and analytics</li>
                <li>• Create and configure new clinic tenants</li>
                <li>• Full system administration capabilities</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-yellow-900">Important Note:</h4>
                  <p className="text-yellow-700 text-sm mt-1">
                    Regular clinic admins will NOT see the tenant switcher. Only super admins can switch between clinics.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              {upgradeStatus === 'idle' && (
                <button
                  onClick={handleUpgrade}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium"
                >
                  <FiShield className="h-4 w-4" />
                  Upgrade to Super Admin
                </button>
              )}
              
              {upgradeStatus === 'running' && (
                <div className="flex items-center gap-2 text-purple-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span>Upgrading to super admin...</span>
                </div>
              )}
              
              {upgradeStatus === 'success' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <FiCheck className="h-5 w-5" />
                    <span className="font-medium">Super Admin Upgrade Complete!</span>
                  </div>
                  <p className="text-sm text-gray-600">Redirecting to admin panel...</p>
                </div>
              )}
              
              {upgradeStatus === 'error' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
                    <FiAlertCircle className="h-5 w-5" />
                    <span className="font-medium">Upgrade Failed</span>
                  </div>
                  <button
                    onClick={handleUpgrade}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-900">Upgrade Error</h4>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminUpgrade;
